import React, { useEffect, useContext } from 'react';
import {
    Grid,
    Table,
    PagingPanel,
    TableFilterRow,
    TableRowDetail,
    TableHeaderRow,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-bootstrap4';
import { FilteringState, PagingState, CustomPaging, RowDetailState, SortingState, SelectionState } from '@devexpress/dx-react-grid';
import axios from 'axios';

import {
    InternalHoursFilterOperations,
    BirthDateFilterOperations,
    SkillsFilterOperations,
    StartDateFilterOperations,
    EndDateFilterOperations,
} from '../shared/FilterOperations';
import { FilterColumns, SortTable, ChangeTablePage, HandlePageLoading } from '../shared/TableEventHelpers';
import NumberOfSearchResults from '../shared/NumberOfSearchResults';
import ConvertTableToExcel from '../shared/ConvertTableToExcel';
import { InitialCommonCommands } from '../shared/PageStartup';
import FilterResetter from '../shared/FilterResetter';
import ColumnChooser from '../shared/ColumnChooser';
import TableHeader from '../shared/TableHeader';
import TableFooter from '../shared/TableFooter';
import NoDataRow from '../shared/NoDataRow';
import TableRow from '../shared/TableRow';

import FilterCell from './FilterCell';
import RowDetail from './RowDetail';
import TableCell from './TableCell';

import { DataContext } from '../../../stores/emp-table-store';
import { DataContext as MainStoreContext } from '../../../store';
import Loading from '../../Loading';

import { useTranslation } from 'react-i18next';
import { translateOptionValue} from '../../exl/shared/translations';


const CancelToken = axios.CancelToken;
let cancel;

const EmployeeTable = () => {
    const { store, dispatch } = useContext(DataContext);
    const mainStore = useContext(MainStoreContext);
    const { t } = useTranslation(['employeeTable', 'shared']);

    const {
        isLoading,
        tableSize,
        columnName,
        filters,
        direction,
        pageSize,
        error,
        employees,
        columns,
        defaultFilters,
        hiddenColumns,
        showCustomCols,
        currentPage,
        sortInfo,
        expandedRowIDs,
        requestUri,
        tableColumnExtensions,
        defaultTableColumnExtensions
    } = store;

    useEffect(() => {
        if (!hiddenColumns.includes('hours')) fetchData('empHours');
        else fetchData();
        translateColumnTitles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnName, currentPage, filters, direction, pageSize]);

    useEffect(() => {
        const handleHiddenColumns = () => {
            const hidColumns = FilterColumns(columns, hiddenColumns, showCustomCols, defaultTableColumnExtensions, true, dispatch);
            if (hidColumns !== null) {
                dispatch({ type: 'HIDDEN_COLUMNS', payload: hidColumns });
            }
        };

        handleHiddenColumns();
        window.addEventListener('resize', handleHiddenColumns);

        return function cleanup() {
            window.removeEventListener('resize', handleHiddenColumns);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, showCustomCols, dispatch]);

    useEffect(() => {
        HandlePageLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
        InitialCommonCommands(t);
    }, []);

    const handleSortChange = (sorting) => {
        const sortColumn = sorting[0].columnName;
        let shouldSort = true;

        if (sortColumn === 'endDate') {
            shouldSort =
                filters.filter(
                    (filter) =>
                        (filter.columnName === 'status' && filter.operation.includes('hasQuit')) ||
                        (filter.columnName === 'status' && filter.operation.includes('showAll'))
                ).length > 0;
        }
        if (shouldSort) {
            SortTable(sorting, sortInfo, dispatch);
        }
    };

    const handlePageSizeChange = (pageSize) => ChangeTablePage(pageSize, dispatch, currentPage, tableSize);

    const handleHiddenColumnChange = (hiddenColumns) => {
        dispatch({ type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS', hiddenColumns, showCustomCols: true });

        if (!hiddenColumns.includes('hours')) {
            fetchData('empHours');
        }
    };

    const translateColumnTitles = () => {
        columns.map((col) => {
            col.title = t(col.title);
        })
    }

    function fetchData(uriExtension) {
        cancel && cancel();

        let uri = 'api/employee/';
        if (uriExtension) uri += uriExtension + '/';
        uri += `${columnName}/${direction}/${currentPage}/${pageSize}`;

        const strFilters = JSON.stringify(filters);

        const newRequestUri = uri + '/' + strFilters;

        if (newRequestUri === requestUri && mainStore.store.loadedTabs['/employees']) {
            return;
        }
        dispatch({ type: 'SET_ISLOADING_TRUE' });
        dispatch({ type: 'SET_REQUEST_URI', payload: newRequestUri });
        try {
            axios
                .get(uri, {
                    params: {
                        filters: strFilters,
                    },
                    cancelToken: new CancelToken((c) => {
                        cancel = c;
                    }),
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json;charset=UTF-8',
                    },
                })
                .then((response) => {
                    const { rows, totalRows } = response.data;
                    
                    dispatch({ type: 'FETCH_SUCCESS', employees: rows, totalRows });
                })
                .catch((thrown) => {
                    if (axios.isCancel(thrown)) {
                        //TODO Something when request is cancelled?
                    } else {
                        const error = thrown.response;
                        dispatch({ type: 'FETCH_ERROR', payload: error });
                    }
                });
        } catch (error) {
            console.error(error);
            dispatch({ type: 'SET_ISLOADING_FALSE' });
        }
    }

    return (
        <Grid columns={columns} rows={employees} className='padding-right-60px'>
            <PagingState
                pageSize={pageSize}
                currentPage={currentPage - 1}
                onPageSizeChange={handlePageSizeChange}
                onCurrentPageChange={(currentPage) => {
                    dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
            />
            <SortingState onSortingChange={handleSortChange} defaultSorting={[{ columnName, direction }]} />
            <CustomPaging totalCount={tableSize} />
            <SelectionState />
            <FilteringState defaultFilters={defaultFilters} />
            <RowDetailState
                expandedRowIds={expandedRowIDs}
                onExpandedRowIdsChange={(ids) => {
                    dispatch({ type: 'UPDATE_ROW_DETAILS', payload: ids });
                }}
            />

            <Table
                columnExtensions={tableColumnExtensions}
                cellComponent={TableCell}
                rowComponent={(props) => TableRow(props, 'employee')}
                noDataRowComponent={(props) => NoDataRow(isLoading, error, props)}
            />

            <InternalHoursFilterOperations />
            <SkillsFilterOperations />
            <BirthDateFilterOperations />
            <StartDateFilterOperations />
            <EndDateFilterOperations />

            <TableRowDetail contentComponent={RowDetail} cellComponent={RowDetailCell} />
            <TableFilterRow showFilterSelector cellComponent={FilterCell} />
            <TableHeaderRow showSortingControls />
            <PagingPanel pageSizes={[10, 25, 50, 10000]} />

            <TableColumnVisibility hiddenColumnNames={hiddenColumns} />

            <TableHeader tableSize={tableSize}>
                <ConvertTableToExcel filters={filters} columns={columns} hiddenColumns={hiddenColumns} table='employee' />
                <ColumnChooser
                    hiddenColumns={hiddenColumns}
                    columns={columns}
                    onHiddenColumnChange={(hiddenColumns) => {
                        handleHiddenColumnChange(hiddenColumns);
                    }}
                    onReset={() => {
                        dispatch({ type: 'RESET_HIDDEN_COLUMNS' });
                    }}
                />
                <FilterResetter DataContext={DataContext} />
            </TableHeader>

            {isLoading ? <Loading /> : null}

            <TableFooter>
                <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
            </TableFooter>
        </Grid>
    );
};

export default EmployeeTable;

const RowDetailCell = ({ ...restProps }) => {
    return <TableRowDetail.Cell {...restProps} className='bg-rowdetail' />;
};
